import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatCents, useAccounts, useCategories } from "@/lib/money";
import { Plus, Trash2, ArrowRight, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function MoneyTransactions() {
  const { accounts } = useAccounts();
  const { categories } = useCategories();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountFilter, setAccountFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (accountFilter !== "all") params.set("account_id", accountFilter);
    if (directionFilter !== "all") params.set("direction", directionFilter);
    if (q) params.set("q", q);
    params.set("limit", "500");
    const r = await api.get(`/money/transactions?${params.toString()}`);
    setTxs(r.data);
    setLoading(false);
  }, [accountFilter, directionFilter, q]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!confirm("Delete this transaction?")) return;
    await api.delete(`/money/transactions/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="money-transactions" className="space-y-4">
      {/* Filters */}
      <div className="rounded-hero bg-surface-container p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-surface rounded-full px-4 py-2 flex-1 min-w-[220px] border border-outline-variant">
          <Search className="w-4 h-4 text-surface-onVariant" />
          <input
            data-testid="tx-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search payee…"
            className="bg-transparent outline-none flex-1 text-sm text-surface-on placeholder:text-surface-onVariant"
          />
          {q && (
            <button onClick={() => setQ("")} className="p-1 rounded-full hover:bg-primary/10">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <Select value={accountFilter} onValueChange={setAccountFilter}>
          <SelectTrigger data-testid="tx-account-filter" className="w-40 rounded-full bg-surface border border-outline-variant text-sm h-10"><SelectValue placeholder="Account" /></SelectTrigger>
          <SelectContent className="rounded-2xl bg-surface">
            <SelectItem value="all">All accounts</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={directionFilter} onValueChange={setDirectionFilter}>
          <SelectTrigger data-testid="tx-direction-filter" className="w-36 rounded-full bg-surface border border-outline-variant text-sm h-10"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent className="rounded-2xl bg-surface">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
          </SelectContent>
        </Select>
        <button
          data-testid="tx-add-open"
          onClick={() => setAddOpen(true)}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-5 py-2.5 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
        >
          <Plus className="w-4 h-4" /> Add transaction
        </button>
      </div>

      {/* Table */}
      <div className="rounded-hero bg-surface-container overflow-hidden">
        <table className="w-full text-sm" data-testid="tx-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-surface-onVariant">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Payee</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Account</th>
              <th className="px-4 py-3 font-medium text-right">Amount</th>
              <th className="px-4 py-3 font-medium w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-sm text-surface-onVariant">Loading…</td></tr>}
            {!loading && txs.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-sm text-surface-onVariant">No transactions match.</td></tr>}
            {txs.map((t) => {
              const acc = accounts.find((a) => a.id === t.account_id);
              const acc2 = t.transfer_account_id ? accounts.find((a) => a.id === t.transfer_account_id) : null;
              const cat = categories.find((c) => c.id === t.category_id);
              const isTransfer = t.direction === "transfer";
              const isIncome = t.direction === "income";
              const sign = isIncome ? "+" : isTransfer ? "" : "-";
              const amtTone = isIncome ? "text-primary" : isTransfer ? "text-surface-onVariant" : "text-tertiary";
              return (
                <tr key={t.id} data-testid={`tx-row-${t.id}`} className="border-t border-outline-variant hover:bg-primary/5 transition-colors duration-200 ease-md">
                  <td className="px-4 py-3 text-surface-onVariant whitespace-nowrap">{t.date}</td>
                  <td className="px-4 py-3">
                    <div className="text-surface-on font-medium">{t.payee}</div>
                    {t.notes && <div className="text-xs text-surface-onVariant">{t.notes}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {isTransfer ? (
                      <span className="text-xs text-surface-onVariant">Transfer</span>
                    ) : cat ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-surface-onVariant">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </span>
                    ) : <span className="text-xs text-surface-onVariant">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-surface-onVariant whitespace-nowrap">
                    {acc?.name}
                    {acc2 && <> <ArrowRight className="inline w-3 h-3 mx-1" /> {acc2.name}</>}
                  </td>
                  <td className={cn("px-4 py-3 text-right font-medium tabular-nums whitespace-nowrap", amtTone)}>
                    {sign}{formatCents(t.amount_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`tx-delete-${t.id}`}
                      onClick={() => del(t.id)}
                      className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger transition-colors duration-200 ease-md"
                      aria-label="Delete transaction"
                    ><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AddTransactionDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} accounts={accounts} categories={categories} />
    </div>
  );
}

function AddTransactionDialog({ open, onOpenChange, onSaved, accounts, categories }) {
  const [direction, setDirection] = useState("expense");
  const [account_id, setAccountId] = useState("");
  const [transfer_account_id, setTransferAccountId] = useState("");
  const [category_id, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payee, setPayee] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const catsForDirection = categories.filter((c) => (direction === "income" ? c.kind === "income" : c.kind === "expense"));

  const reset = () => {
    setDirection("expense"); setAccountId(""); setTransferAccountId("");
    setCategoryId(""); setAmount(""); setPayee(""); setNotes("");
    setDate(new Date().toISOString().slice(0, 10));
  };

  const save = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (cents <= 0) return toast.error("Enter an amount");
    if (!account_id) return toast.error("Pick an account");
    if (direction === "transfer" && !transfer_account_id) return toast.error("Pick the destination account");
    if (direction !== "transfer" && !category_id) return toast.error("Pick a category");
    setSaving(true);
    try {
      const payload = { account_id, date, amount_cents: cents, direction, payee: payee || "—", notes: notes || null, cleared: true };
      if (direction === "transfer") payload.transfer_account_id = transfer_account_id;
      else payload.category_id = category_id;
      await api.post("/money/transactions", payload);
      toast.success("Transaction added");
      reset(); onOpenChange(false); onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-surface rounded-hero border-none p-6" data-testid="tx-add-dialog">
        <DialogHeader>
          <DialogTitle className="text-surface-on text-lg font-medium">Add transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          {/* Direction pills */}
          <div className="inline-flex bg-surface-container rounded-full p-1 w-full">
            {["expense", "income", "transfer"].map((k) => (
              <button
                key={k} type="button"
                data-testid={`tx-dir-${k}`}
                onClick={() => setDirection(k)}
                className={cn(
                  "flex-1 px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors duration-200 ease-md",
                  direction === k ? "bg-primary text-primary-foreground" : "text-surface-on hover:bg-primary/8"
                )}
              >{k}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount" prefix="$">
              <input data-testid="tx-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" placeholder="0.00" required />
            </Input>
            <Input label="Date">
              <input data-testid="tx-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" required />
            </Input>
          </div>
          <Input label="Payee">
            <input data-testid="tx-payee" value={payee} onChange={(e) => setPayee(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" placeholder={direction === "transfer" ? "Transfer" : "e.g. Woolworths"} />
          </Input>
          <div>
            <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">
              {direction === "transfer" ? "From account" : "Account"}
            </div>
            <Select value={account_id} onValueChange={setAccountId}>
              <SelectTrigger data-testid="tx-account" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue placeholder="Choose account" /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {direction === "transfer" ? (
            <div>
              <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">To account</div>
              <Select value={transfer_account_id} onValueChange={setTransferAccountId}>
                <SelectTrigger data-testid="tx-transfer-to" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue placeholder="Destination account" /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  {accounts.filter((a) => a.id !== account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">Category</div>
              <Select value={category_id} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="tx-category" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface max-h-72">
                  {catsForDirection.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 rounded-full text-sm font-medium text-surface-on hover:bg-primary/8 transition-colors duration-200 ease-md">Cancel</button>
            <button
              data-testid="tx-save"
              type="submit" disabled={saving}
              className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md"
            >
              {saving ? "Saving…" : "Save transaction"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, prefix, children }) {
  return (
    <div>
      <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">{label}</div>
      <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5 transition-colors duration-200 ease-md">
        {prefix && <span className="text-surface-onVariant mr-1">{prefix}</span>}
        {children}
      </div>
    </div>
  );
}
